import type { ReactNode } from 'react';
import { AlertTriangle, Mic, Volume1, VolumeX } from 'lucide-react';
import { t } from '@/i18n';
import type { TranslationKey } from '@/i18n';
import { useMicrophone, useMicrophoneLease } from '@/hooks/useAudio';
import { Button, Card } from './ui';
import './MicGate.css';

interface MicGateProps {
  children: ReactNode;
  /** Extra note shown under the ready state, e.g. a privacy line. */
  footer?: ReactNode;
}

/**
 * Guards every screen that needs the microphone.
 *
 * There is no generic "something went wrong" here: each failure names what
 * happened and what the player can do about it, because a beginner who is told
 * only that it failed will assume the guitar or their playing is the problem.
 */
export function MicGate({ children, footer }: MicGateProps) {
  const { state, enable } = useMicrophone();
  // This screen needs the microphone for as long as it is mounted.
  useMicrophoneLease();

  if (state.status === 'ready') {
    const warning = calibrationWarning(state.calibration?.quality);
    return (
      <>
        {warning && (
          <Card className="mic-gate__warning">
            <div className="mic-gate__warning-row">
              <Volume1 size={20} aria-hidden="true" />
              <p>{t(warning)}</p>
            </div>
          </Card>
        )}
        {children}
        {footer}
      </>
    );
  }

  if (state.status === 'requesting' || state.status === 'calibrating') {
    return (
      <Card>
        <div className="mic-gate">
          <div className="mic-gate__icon mic-gate__icon--neutral">
            <Mic size={26} aria-hidden="true" />
          </div>
          <p className="mic-gate__title">{t('mic.checking')}</p>
          <p className="mic-gate__hint">{t('mic.privacy')}</p>
        </div>
      </Card>
    );
  }

  // Not asked yet. This is the normal first step, not a failure — showing a
  // warning triangle here would tell a brand-new player that something is
  // broken before they have done anything at all.
  if (state.status === 'idle') {
    return (
      <Card>
        <div className="mic-gate">
          <div className="mic-gate__icon mic-gate__icon--neutral">
            <Mic size={26} aria-hidden="true" />
          </div>
          <p className="mic-gate__title">{t('mic.invite')}</p>
          <p className="mic-gate__hint">{t('mic.inviteHint')}</p>
          <Button onClick={() => void enable()} block>
            <Mic size={18} aria-hidden="true" />
            {t('mic.enable')}
          </Button>
        </div>
      </Card>
    );
  }

  const { titleKey, hintKey } = describeStatus(state.status);

  return (
    <Card>
      <div className="mic-gate">
        <div className="mic-gate__icon mic-gate__icon--alert">
          {state.status === 'denied' ? (
            <VolumeX size={26} aria-hidden="true" />
          ) : (
            <AlertTriangle size={26} aria-hidden="true" />
          )}
        </div>
        <p className="mic-gate__title">{t(titleKey)}</p>
        <p className="mic-gate__hint">{t(hintKey)}</p>
        {state.status !== 'unsupported' && (
          <Button onClick={() => void enable()} block>
            <Mic size={18} aria-hidden="true" />
            {state.status === 'denied' ? t('mic.enable') : t('mic.retry')}
          </Button>
        )}
        <p className="mic-gate__privacy">{t('mic.privacy')}</p>
      </div>
    </Card>
  );
}

function describeStatus(status: string): { titleKey: TranslationKey; hintKey: TranslationKey } {
  switch (status) {
    case 'denied':
      return { titleKey: 'mic.denied', hintKey: 'mic.deniedHint' };
    case 'unavailable':
      return { titleKey: 'mic.unavailable', hintKey: 'mic.unavailableHint' };
    case 'unsupported':
      return { titleKey: 'mic.unsupported', hintKey: 'mic.unsupportedHint' };
    default:
      return { titleKey: 'mic.error', hintKey: 'mic.unavailableHint' };
  }
}

function calibrationWarning(quality?: string): TranslationKey | null {
  switch (quality) {
    case 'noisy':
      return 'mic.noisy';
    case 'quiet':
      return 'mic.quiet';
    case 'clipping':
      return 'mic.clipping';
    default:
      return null;
  }
}
